import { useEffect, useId, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { X } from "lucide-react";
import { TOUCH_SQUARE, buttonClass } from "@/components/buttonStyles";
import { WonBidStepper } from "@/components/bond-submission/WonBidStepper";
import type { BondSubmission } from "@/lib/brokerBonds";
import { wonSubmittedMessage } from "@/lib/wonBid";

// BRK-S421 — the Bid Result dialog's Won opens this: the won-bid stepper
// (Final price, Bid results, Confirm bond forms, Award letter) over the
// submission page, full screen on a phone. The same `WonBidStepper` the
// `/won` route hosts.
//
// Success only from the 2xx (NN #7): the dialog says what the RESPONSE's
// `workflowStatus` earned (`wonSubmittedMessage`) and hands the page the
// order the PUT answered.

export function WonBidStepperDialog({
  order,
  open,
  onClose,
  onUpdated,
}: {
  order: BondSubmission;
  open: boolean;
  onClose: () => void;
  /** The order a 2xx answered with: the page shows it. */
  onUpdated: (order: BondSubmission) => void;
}) {
  const titleId = useId();
  const subId = useId();
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });
  const [recorded, setRecorded] = useState<string | null>(null);
  // A fresh flow each time it opens.
  useEffect(() => {
    if (open) setRecorded(null);
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      fullScreen={phone}
      scroll="paper"
      aria-labelledby={`${titleId} ${subId}`}
    >
      <div className="flex items-start gap-3 border-b border-brk-border p-4">
        <div className="flex min-w-0 flex-1 flex-col">
          <h2 id={titleId} className="text-heading text-brk-text">
            Won bid
          </h2>
          <p id={subId} className="break-words text-sm text-brk-dim">
            {[order.number, order.accountLegalName]
              .filter((part): part is string => !!part)
              .join(" · ")}
          </p>
        </div>
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className={`rounded-md p-2 text-brk-dim hover:bg-brk-bg hover:text-brk-text ${TOUCH_SQUARE}`}
        >
          <X aria-hidden="true" className="h-4 w-4" />
        </button>
      </div>
      <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
        {recorded !== null ? (
          <div className="flex min-w-0 flex-col gap-4">
            <p
              role="status"
              className="text-sm text-green-700 dark:text-green-300"
            >
              <span aria-hidden="true">✓ </span>
              <span>{recorded}</span>
            </p>
            <button
              type="button"
              onClick={onClose}
              className={`${buttonClass()} self-end`}
            >
              Done
            </button>
          </div>
        ) : (
          <WonBidStepper
            order={order}
            headingLevel={3}
            onSaved={onUpdated}
            onRecorded={(fresh, bonds) => {
              onUpdated(fresh);
              setRecorded(wonSubmittedMessage(fresh.status, bonds));
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
